// Slice H2 — curated template index.
//
// Single source of truth for the curated starter templates that ship
// with the app. Consumers (the gallery modal + tests + entry-point
// surfaces) import `CURATED_TEMPLATES` to enumerate available
// templates, then call `findCuratedTemplateBySlug` to resolve a
// specific one for loading.
//
// Adding a template:
//   1. Author a new module `src/templates/<slug>.ts` that exports a
//      `CuratedTemplate` constant.
//   2. Add it to the `CURATED_TEMPLATES` array below in the order it
//      should appear in the gallery (left → right, top → bottom).
//   3. Add a test entry in `scripts/test-conversion.mjs` h2 group
//      to assert it's structurally valid.
//
// v1 ships with exactly 4 templates per the H2 design doc — quality
// over quantity.

export type { CuratedTemplate, TemplateFamily, TemplatePropFirmRef } from './types';
export { templateBlockMetadata, namespacedBlockId } from './types';

import type { CuratedTemplate } from './types';
import { TREND_EMA_PULLBACK } from './trendEmaPullback';
import { MR_BOLLINGER_FADE } from './meanReversionBollinger';
import { BREAKOUT_DONCHIAN } from './breakoutDonchian';
import { PROPFIRM_INTRADAY_FTMO } from './propfirmIntradayFtmo';

export { TREND_EMA_PULLBACK, MR_BOLLINGER_FADE, BREAKOUT_DONCHIAN, PROPFIRM_INTRADAY_FTMO };

/** All curated starter templates, in gallery display order. v1 ships
 *  4 templates — one per family bucket (trend / mean-reversion /
 *  breakout / prop-firm). */
export const CURATED_TEMPLATES: readonly CuratedTemplate[] = Object.freeze([
  TREND_EMA_PULLBACK,
  MR_BOLLINGER_FADE,
  BREAKOUT_DONCHIAN,
  PROPFIRM_INTRADAY_FTMO,
]);

/** Look up a template by slug. Returns null when the slug is unknown
 *  so callers can gracefully handle stale localStorage references
 *  (e.g. a user upgrades the app and an old "loaded template" slug
 *  no longer exists). */
export function findCuratedTemplateBySlug(slug: string): CuratedTemplate | null {
  for (const tpl of CURATED_TEMPLATES) {
    if (tpl.slug === slug) return tpl;
  }
  return null;
}

/** Convenience: filter by family. Used by the gallery's family-pill
 *  filter UI. */
export function curatedTemplatesByFamily(
  family: CuratedTemplate['family'] | 'all',
): readonly CuratedTemplate[] {
  if (family === 'all') return CURATED_TEMPLATES;
  return CURATED_TEMPLATES.filter(t => t.family === family);
}
